import { watch } from 'vue'
import { booleanCookieSerializer } from '~/utils/cookie-serializers'
import {
  normalizeSidebarUserPref,
  requestHasCookie,
  resolveSidebarCollapsed,
  resolveSidebarUserPref,
  resolveLayoutSidebarCollapsed,
  sidebarCollapsedHtmlAttr,
  syncSidebarCollapsedDomAttr,
  SIDEBAR_COLLAPSED_COOKIE,
  SIDEBAR_COOKIE_OPTIONS,
  SIDEBAR_STATE_KEY,
  SIDEBAR_USER_PREF_COOKIE,
} from '~/utils/dashboard-sidebar-state'

const MOBILE_CWV_TARGET_PATHS = [
  '/',
  '/zh',
  '/ru',
  '/id',
  '/ar',
  '/cn',
  '/talking-photo-ai',
  '/talking-avatar',
  '/ai-singing-photo',
  '/talking-animals',
  '/ai-baby-podcast',
  '/avatar-dialogue',
  '/pricing',
  '/ai-avatar-generator',
  '/ai-influencer-generator',
  '/ai-news-anchor',
  '/baby-talking',
  '/cartoon-singing',
  '/talking-cartoon',
  '/ai-dubbing',
  '/baby-singing',
  '/ai-human-generator',
  '/ai-animate-face-lip-sync',
  '/ai-image-generator',
  '/ai-image-to-video',
  '/audio-cleaner',
  '/audio-enhancer',
  '/voice-cloning',
  '/product-avatar',
  '/account',
  '/history-lipsync',
  '/my-creations',
  '/refer',
  '/terms',
  '/privacy-policy',
]

const mobileCwvTargetPathMap = Object.fromEntries(
  MOBILE_CWV_TARGET_PATHS.map((path) => [path, 1]),
)

const sidebarFirstPaintScript =
  `(function(){try{var q=${JSON.stringify(mobileCwvTargetPathMap)};function a(v){document.documentElement.setAttribute('data-sidebar-collapsed',v);if(document.body)document.body.setAttribute('data-sidebar-collapsed',v)}function t(){var x=(location.pathname||'/').split('?')[0].split('#')[0].replace(/\\/+$/,'')||'/';if(q[x])document.documentElement.setAttribute('data-lip-mobile-cwv-target','1')}if(window.innerWidth<768){a('0');t();return}var p='lip-dashboard-sidebar-user-pref',l='lip-dashboard-sidebar-collapsed',r={'/pricing':1,'/terms':1,'/privacy-policy':1,'/refer':1,'/account':1},o={en:1,ru:1,ja:1,id:1,ar:1,cn:1,de:1,es:1,fr:1,it:1,kr:1,nl:1,pt:1,tr:1,zh:1};function c(n){var m=document.cookie.match('(?:^|; )'+n+'=([^;]*)');return m?decodeURIComponent(m[1]):''}function n(v){v=(v||'').replace(/^[\"']|[\"']$/g,'').trim();return v==='collapsed'||v==='expanded'?v:''}function w(v){try{sessionStorage.setItem(p,v)}catch(e){}try{document.cookie=p+'='+encodeURIComponent(v)+'; Path=/; Max-Age=31536000; SameSite=Lax'+(location.protocol==='https:'?'; Secure':'')}catch(e){}}function g(v){if(v==null)return '';try{v=JSON.parse(v)}catch(e){}return v===true||v==='true'||v==='1'||v===1?'collapsed':v===false||v==='false'||v==='0'||v===0?'expanded':''}function u(){var v=n(c(p));if(v)return v;try{v=n(sessionStorage.getItem(p)||'')}catch(e){}if(v)return v;try{v=g(localStorage.getItem(l));if(v)w(v)}catch(e){}return v}function b(){var v=c(l);return v==='true'||v==='1'?1:v==='false'||v==='0'?0:null}function d(path){var parts=(path||'/').split('?')[0].split('#')[0].split('/').filter(Boolean);if(parts.length&&o[parts[0]])parts.shift();return parts.length?'/'+parts.join('/'):'/'}var pref=u(),path=d(location.pathname),collapsed=pref==='collapsed'?1:pref==='expanded'?0:r[path]?1:0;if(!pref){var legacy=b();if(legacy!==null)collapsed=legacy}a(String(collapsed))}catch(e){}})();`

const sidebarDomSyncScript =
  "(function(){function n(v){return v==='true'?'1':v==='false'?'0':v}function ok(v){return v==='1'||v==='0'}function set(el,name,v){if(el&&el.getAttribute(name)!==v)el.setAttribute(name,v)}function s(v){set(document.documentElement,'data-sidebar-collapsed',v);if(document.body)set(document.body,'data-sidebar-collapsed',v)}function run(){try{var el=document.getElementById('layout-sidebar'),v=n(document.documentElement.getAttribute('data-sidebar-collapsed')),a=el?n(el.getAttribute('data-collapsed')):'';if(ok(v)){s(v);set(el,'data-collapsed',v);return}if(ok(a)){set(el,'data-collapsed',a);s(a)}}catch(e){}}if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',run,{once:true});else run()})();"

/**
 * 侧栏偏好唯一初始化点（enforce: pre）。壳层 UI 见 useDashboardShellLayout。
 * @see docs/dashboard-shell-state.md
 */
export default defineNuxtPlugin({
  name: 'lip-sidebar-state-init',
  enforce: 'pre',
  order: -5,
  setup(nuxtApp) {
    const route = useRoute()

    const sidebarUserPref = useCookie(SIDEBAR_USER_PREF_COOKIE, {
      default: () => '',
      ...SIDEBAR_COOKIE_OPTIONS,
      watch: true,
    })

    const legacyCollapsedCookie = useCookie(SIDEBAR_COLLAPSED_COOKIE, {
      default: () => false,
      ...SIDEBAR_COOKIE_OPTIONS,
      watch: false,
      serializer: booleanCookieSerializer,
    })

    const cookieHeader = import.meta.server
      ? useRequestHeaders(['cookie']).cookie || ''
      : typeof document !== 'undefined'
        ? document.cookie
        : ''

    let pref = resolveSidebarUserPref(cookieHeader, sidebarUserPref.value)
    if (pref && sidebarUserPref.value !== pref) {
      sidebarUserPref.value = pref
    }

    if (
      pref !== 'collapsed' &&
      pref !== 'expanded' &&
      requestHasCookie(SIDEBAR_COLLAPSED_COOKIE, cookieHeader)
    ) {
      const migrated = normalizeSidebarUserPref(pref, legacyCollapsedCookie.value === true)
      if (migrated) {
        sidebarUserPref.value = migrated
        pref = migrated
      }
    }

    const resolved = resolveSidebarCollapsed(route, pref)
    const sidebarCollapsed = useState(SIDEBAR_STATE_KEY, () => resolved)
    sidebarCollapsed.value = resolved

    if (nuxtApp.payload?.state) {
      nuxtApp.payload.state[SIDEBAR_STATE_KEY] = resolved
    }

    /** 宽度由 html[data-sidebar-collapsed] 关键 CSS 控制，须随 useState 响应式更新，勿写死 SSR 初值 */
    useHead(() => ({
      htmlAttrs: {
        'data-sidebar-collapsed': sidebarCollapsedHtmlAttr(
          resolveLayoutSidebarCollapsed(sidebarCollapsed.value),
        ),
      },
      bodyAttrs: {
        'data-sidebar-collapsed': sidebarCollapsedHtmlAttr(
          resolveLayoutSidebarCollapsed(sidebarCollapsed.value),
        ),
      },
      script: [
        {
          key: 'lip-sidebar-first-paint',
          innerHTML: sidebarFirstPaintScript,
          tagPosition: 'head',
        },
        {
          key: 'lip-sidebar-dom-sync',
          innerHTML: sidebarDomSyncScript,
          tagPosition: 'bodyClose',
        },
      ],
    }))

    if (import.meta.client) {
      watch(
        sidebarCollapsed,
        (collapsed) => {
          syncSidebarCollapsedDomAttr(collapsed)
        },
        { immediate: true },
      )

      const applyClientPref = () => {
        const latestPref = resolveSidebarUserPref(document.cookie, sidebarUserPref.value)
        if (latestPref && sidebarUserPref.value !== latestPref) {
          sidebarUserPref.value = latestPref
        }
        const latest = resolveSidebarCollapsed(route, latestPref || sidebarUserPref.value)
        sidebarCollapsed.value = latest
        if (nuxtApp.payload?.state) {
          nuxtApp.payload.state[SIDEBAR_STATE_KEY] = latest
        }
        syncSidebarCollapsedDomAttr(latest)
      }

      applyClientPref()
      nuxtApp.hook('app:mounted', applyClientPref)
    }
  },
})
